<template>
  <button class="btn"
          :class="[width, color, type, { 'disabled': disabled, btnLoading: btnLoading }]"
          :disabled="disabled"
          :type="buttonType"
          :aria-disabled="disabled"
  >
    <slot v-if="!btnLoading"></slot>
  </button>
</template>

<script setup lang="ts">
interface Props {
  width?: string
  color?: string
  disabled?: boolean
  type?: string
  buttonType?: 'button' | 'reset' | 'submit'
  btnLoading?: boolean
}
withDefaults(defineProps<Props>(), {
  disabled: false,
  btnLoading: false,
})
</script>

<script lang="ts">
export default {
  name: 'vita-button'
}
</script>

<style scoped lang="scss">
@use '../../static/style/variables.scss' as *;

.btn {
  background: linear-gradient(180deg, #30A8FF 0%, #007AFF 100%);
  width: 100%;
  border: 0;
  color: $white;
  height: 34px;
  font-family: inherit;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.btnLoading {
    pointer-events: none;
    position: relative;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 24px;
      height: 24px;
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDIyQzE3LjQyMSAyMiAyMiAxNy40MjEgMjIgMTJIMjBDMjAgMTYuMzM3IDE2LjMzNyAyMCAxMiAyMEM3LjY2MyAyMCA0IDE2LjMzNyA0IDEyQzQgNy42NjMgNy42NjMgNCAxMiA0VjJDNi41NzkgMiAyIDYuNTggMiAxMkMyIDE3LjQyMSA2LjU3OSAyMiAxMiAyMloiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl8xNTgzXzExOTQxKSIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyXzE1ODNfMTE5NDEiIHgxPSIxMiIgeTE9IjIiIHgyPSIxMiIgeTI9IjIyIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IndoaXRlIi8+CjxzdG9wIG9mZnNldD0iMSIgc3RvcC1jb2xvcj0iI0Q3RUVGRiIvPgo8L2xpbmVhckdyYWRpZW50Pgo8L2RlZnM+Cjwvc3ZnPgo=');
      animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
      to {
        transform: translate(-50%, -50%) rotate(360deg);
      }
    }

    &.red::after {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDIyQzE3LjQyMSAyMiAyMiAxNy40MjEgMjIgMTJIMjBDMjAgMTYuMzM3IDE2LjMzNyAyMCAxMiAyMEM3LjY2MyAyMCA0IDE2LjMzNyA0IDEyQzQgNy42NjMgNy42NjMgNCAxMiA0VjJDNi41NzkgMiAyIDYuNTggMiAxMkMyIDE3LjQyMSA2LjU3OSAyMiAxMiAyMloiIGZpbGw9IiNGRjNCMzAiLz4KPC9zdmc+Cg==');
    }

    &.clear::after {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTEyIDIyQzE3LjQyMSAyMiAyMiAxNy40MjEgMjIgMTJIMjBDMjAgMTYuMzM3IDE2LjMzNyAyMCAxMiAyMEM3LjY2MyAyMCA0IDE2LjMzNyA0IDEyQzQgNy42NjMgNy42NjMgNCAxMiA0VjJDNi41NzkgMiAyIDYuNTggMiAxMkMyIDE3LjQyMSA2LjU3OSAyMiAxMiAyMloiIGZpbGw9InVybCgjcGFpbnQwX2xpbmVhcl8xMV8zKSIvPgo8ZGVmcz4KPGxpbmVhckdyYWRpZW50IGlkPSJwYWludDBfbGluZWFyXzExXzMiIHgxPSIxMiIgeTE9IjIiIHgyPSIxMiIgeTI9IjIyIiBncmFkaWVudFVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+CjxzdG9wIHN0b3AtY29sb3I9IiMzMUE3RkIiLz4KPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMUQ2Mzk1Ii8+CjwvbGluZWFyR3JhZGllbnQ+CjwvZGVmcz4KPC9zdmc+Cg==');
    }
  }

  &+.btn {
    margin-left: 20px;
  }

  &.small {
    width: 112px;
  }

  &.black {
    background: $text-color;
  }

  &.red {
    background: $white;
    border: 1px solid $red;
    color: $red;

    &:hover {
      color: $white;
      background: $red;
    }

    &:focus:not(.focus-invisible) {
      box-shadow: 0 0 0 2px #bd2543;
      color: $white;
      background: $red;
    }
    
    &:focus.btnLoading {
      color: $red;
      background: transparent;
      box-shadow: unset;
    }
  }

  &.orange {
    background: linear-gradient(180deg, #FF9E69 0%, #FF6F20 100%);
    border: unset;
    color: $white;
    transition: opacity .15s;

    &.not-available {
      opacity: .50;
      cursor: default;

      &:hover, &:active, &:focus {
        background: linear-gradient(180deg, #FF9E69 0%, #FF6F20 100%);
        border: unset;
        color: $white;
        outline: none;
        box-shadow: none;
      }
    }

    &:hover {
      color: $white;
      background: #FF9E69;
    }

    &:focus {
      background: #FF9E69;
      box-shadow: 0 0 0 2px #FF6F20;
    }
  }

  &.danger {
    background: linear-gradient(180deg, #FF6F67 0%, #FF3B30 100%);
    color: var(--white);
  }

  &.disabled {
    background: rgba(136, 136, 136, .5);
    pointer-events: none;
  }

  &.mini {
    width: 54px;
    height: 22px;
    font-size: 12px;
  }

  &.inline {
    width: auto;
    padding: 0 14px;
  }

  &.medium {
    height: 50px;
    padding: 0 4px;
    font-weight: 600;
    font-size: 18px;
    line-height: 22px;
  }

  &.muted {
    background: #EAEBEF;
    color: rgba(51, 51, 51, 1);

    &:hover, &:focus {
      color: $white;
    }
  }

  &.print-btn {
    min-width: 54px;
    padding: 0 14px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-size: 13px;
    line-height: 16px;

    @media (max-width: 1550px) {
      min-width: auto;
      padding: 0 16px;
    }
  }

  &.print-btn-sm {
    max-width: 260px;
    padding: 0 14px;
  }

  &.print-btn-action {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
  }

  &.rounded {
    border-radius: 8px;
  }

  &:hover {
    background: $blue;
  }

  &:active {
    background: $blue-dark;
  }

  &:focus:not(.focus-invisible) {
    box-shadow: 0 0 0 2px $blue-dark;
    background: $blue;
  }

  &:disabled {
    background: rgba(234, 234, 234, 1);
    border-color: rgba(234, 234, 234, 1);
    color: rgba(51, 51, 51, 1);
    cursor: default;
    pointer-events: none;
  }

  &:hover:disabled {
    background: $text-color-dark;
    border-color: $text-color-dark;
    color: #FFF;
    cursor: default;
    pointer-events: none;
  }

  &.clear {
    background: none;
    height: auto;
    width: auto;
    min-width: auto;

    &:hover:not(.red):not(.orange):not(:disabled) {
      background: transparent;
      opacity: 0.7;
    }

    &:focus:not(.focus-invisible):not(.red):not(.orange):not(:disabled) {
      box-shadow: unset;
      background: transparent;
      opacity: 1;
    }

    &:not(.btnLoading):disabled {
      opacity: 0.5;
    }
  }
}

.datepicker-btn {
  height: 23px;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0;
  min-width: 120px;
}

.datepicker-btn + .datepicker-btn {
  margin: 0;
}

.btn-drop {
  background: #FFFFFF;
  color: #333333;
  font-weight: 500;
  font-size: 12px;
  line-height: 110%;
  letter-spacing: 0;
  margin-left: 0;
  text-align: left;
  padding: 6px 8px;

  &:hover {
    background: #F9F9F9;
    opacity: 1;
  }

  &:focus {
    background: #FFFFFF!important;
    color: #333333;
    box-shadow: unset!important;
  }
}

.no-shadow {
  box-shadow: none;

  &:not(.red):active {
    background: #007AFF;
  }

  &:focus:not(.focus-invisible) {
    box-shadow: none!important;
  }
}
</style>
